// react
import { Suspense, useMemo } from 'react';
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, Link } from '@tanstack/react-router';

// material ui
import { Card, CardContent, Grid, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

// api
import {
  type PortfolioAnalysisSubscriptionOutput,
  useGetSubscriptionsSubscriptionsGetQuery,
} from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisSubscription';
import {
  type ReadAccountDto,
  useAccountsGetCollectionAsyncByqueryOptionsQuery,
} from '@/api/generated/recalc/endpoints/v1/accounts';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';

// feature-components
import PortfolioAnalysisManagementDatagrid from '@/features/service-manager/components/energyxplorer/portfolio-analysis/PortfolioAnalysisManagementDatagrid';

// components
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

type PortfolioAnalysisRow = PortfolioAnalysisSubscriptionOutput & {
  id: number;
  AccountId: number;
  Name: string;
  hasService: boolean;
};

type StatCard = {
  name: string;
  value: number;
};

const isPortfolioSubscription = (
  value: unknown,
): value is PortfolioAnalysisSubscriptionOutput => {
  if (value == null || typeof value !== 'object') {
    return false;
  }

  const candidate = value as { account_id?: unknown };
  return typeof candidate.account_id === 'number';
};

export const Route = createFileRoute(
  '/$accountId/_admin/operations/portfolio-analysis/',
)({
  component: RouteComponent,
  staticData: { crumb: 'glossary:portfolio-analysis' },
});

function RouteComponent() {
  const { t } = useTranslation('portfolio-analysis');
  const selectedAccount = useSelector(getSelectedAccount);

  const { data: accounts = [], isFetching: isAccountsFetching } =
    useAccountsGetCollectionAsyncByqueryOptionsQuery({
      orderby: 'AccountId',
      top: 10000,
    });
  const { data: subscriptionDataRaw, isFetching: isSubscriptionsFetching } =
    useGetSubscriptionsSubscriptionsGetQuery();

  const subscriptionData = useMemo<
    PortfolioAnalysisSubscriptionOutput[]
  >(() => {
    if (!Array.isArray(subscriptionDataRaw)) {
      return [];
    }
    return subscriptionDataRaw.filter(isPortfolioSubscription);
  }, [subscriptionDataRaw]);

  const accountsById = useMemo(() => {
    return new Map<number, ReadAccountDto>(
      accounts.map((account) => [account.AccountId, account]),
    );
  }, [accounts]);

  const rows = useMemo<PortfolioAnalysisRow[]>(() => {
    return subscriptionData.map((subscription) => {
      const account = accountsById.get(subscription.account_id);

      return {
        ...subscription,
        id: subscription.account_id,
        AccountId: account?.AccountId ?? subscription.account_id,
        Name: account?.Name ?? '',
        hasService:
          account?.Services?.some(
            (service) => service.Name === 'PortfolioAnalysis',
          ) ?? false,
      };
    });
  }, [accountsById, subscriptionData]);

  const stats = useMemo<StatCard[]>(() => {
    if (subscriptionData.length === 0) {
      return [];
    }

    const totalHeat = subscriptionData.reduce((acc, g) => {
      return g.heat ? acc + 1 : acc;
    }, 0);

    const totalPower = subscriptionData.reduce((acc, g) => {
      return g.power ? acc + 1 : acc;
    }, 0);

    const totalWater = subscriptionData.reduce((acc, g) => {
      return g.water ? acc + 1 : acc;
    }, 0);

    const totalAccounts = subscriptionData.length;

    return [
      { name: 'Total Accounts', value: totalAccounts },
      { name: 'Total Power Portfolios', value: totalPower },
      { name: 'Total Water Portfolios', value: totalWater },
      { name: 'Total Heat Portfolios', value: totalHeat },
    ];
  }, [subscriptionData]);

  const isLoading = isAccountsFetching || isSubscriptionsFetching;

  return (
    <PageContainer maxWidth="xl" sx={{ py: 2 }}>
      <PageHeader title="Operations / Portfolio Analysis" disableArrow />
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              verticalAlign: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        }
      >
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {stats.map((s, index) => (
            <Grid
              key={index}
              size={{
                lg: 4,
                xl: 2
              }}>
              <ServiceCard {...s} />
            </Grid>
          ))}
          <Grid
            size={{
              lg: 4,
              xl: 2
            }}>
            <Link to={'utility-providers/heat'}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: 'heat.light',
                }}
              >
                <CardContent>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    fontWeight="bold"
                  >
                    {t('utilityProviderPage.title', {
                      type: t('app_bar.heat'),
                    })}
                  </Typography>
                </CardContent>
              </Card>
            </Link>
          </Grid>
          <Grid
            size={{
              lg: 4,
              xl: 2
            }}>
            <Link to={'utility-providers/water'}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: 'water.light',
                }}
              >
                <CardContent>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    fontWeight="bold"
                  >
                    {t('utilityProviderPage.title', {
                      type: t('app_bar.water'),
                    })}
                  </Typography>
                </CardContent>
              </Card>
            </Link>
          </Grid>
        </Grid>
        <PortfolioAnalysisManagementDatagrid
          initialRows={rows}
          isLoading={isLoading}
          account={selectedAccount}
        />
      </Suspense>
    </PageContainer>
  );
}

// TODO: Move this to a separate file if possible. This is a temporary component for displaying the stats on the portfolio analysis page. It can be reused for other stats in the future if needed.
type ServiceCardProps = {
  name: string;
  value: number;
};

const ServiceCard = ({ name, value }: ServiceCardProps) => {
  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            mb: 2,
          }}
        >
          <Typography variant="body1" color="textSecondary" fontWeight="bold">
            {name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
          >
            <Typography variant="h4" color="textPrimary" fontWeight={'bold'}>
              {value}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
